/**
 * Requestimates API - Service Layer
 *
 * Thin wrapper over the authenticated HTTP client for requestimate lookups
 * used by the client-side duplicate-detection guards.
 */

import { HTTPService } from './httpService';

const API = new HTTPService();

/**
 * Every request for an installer + VIN, across ALL locations.
 * Server returns ALL states — callers filter (see util/duplicateDetection.js).
 *
 * @param {string|number} installerId
 * @param {string} vin  (should be normalized/uppercased by the caller)
 * @returns {Promise<Array>} rows: { request_id, request_type, requestor_name,
 *   service_requested, current_state, location_id, locationName, created_date,
 *   due_date, assigned_to }
 */
export async function getRequestsByVin(installerId, vin) {
  const { data } = await API.get(`/requestimatesVIN/${installerId}/${vin}`);
  return Array.isArray(data) ? data : [];
}

/**
 * Draft / incomplete requestimates.
 *
 * A draft is a `requests` row with currentState = 'draft'. It carries a
 * `draft_payload` JSON snapshot of the whole wizard state (the client rehydrates
 * from it on resume) plus denormalized display columns so it renders in the list
 * without parsing JSON. Drafts never count toward badges/financials/dashboards.
 *
 * Request-body shape for create/update:
 *   {
 *     draft_payload,        // full wizard-state object (opaque to server)
 *     draft_step,           // 1..5 — where to resume
 *     draft_owner_id,       // creator (attribution)
 *     // denormalized display columns written onto the request row:
 *     vin, serviceRequested, requestor, assignedTo, requestTotal,
 *     location_id, createdType, createdBy
 *   }
 */

/** Create a new draft. Returns { requestId }. */
export async function saveDraft(body) {
  const { data } = await API.post('/requestimates/draft', body);
  return data;
}

/** Re-save an existing draft. */
export async function updateDraft(id, body) {
  const { data } = await API.put(`/requestimates/draft/${id}`, body);
  return data;
}

/**
 * Load a draft for resume.
 * Returns { requestId, draft_payload, draft_step, draft_owner_id,
 *           draft_locked_by, draft_locked_at, locked_by_name }.
 */
export async function getDraft(id) {
  const { data } = await API.get(`/requestimates/draft/${id}`);
  return data;
}

/** Hard-delete a draft (explicit delete, or cleanup after finalize). */
export async function deleteDraft(id) {
  await API.delete(`/requestimates/draft/${id}`);
  return true;
}

/**
 * Acquire the single-editor lock. Resolves { ok: true } when granted.
 * Rejects with a 409 whose response body is { lockedBy, lockedByName } when
 * another user holds an unexpired lock — callers should catch and surface that.
 */
export async function lockDraft(id) {
  const { data } = await API.post(`/requestimates/draft/${id}/lock`, {});
  return data;
}

/** Release the edit lock. Best-effort — never throws (called on route-away). */
export async function unlockDraft(id) {
  try {
    await API.post(`/requestimates/draft/${id}/unlock`, {});
  } catch (err) {
    // Route-away / unmount path — losing the unlock just leaves a lock that
    // expires via the server TTL. Not worth surfacing.
    console.warn('unlockDraft failed (will expire via TTL):', err);
  }
  return true;
}
